import React, { useState } from "react";
import { Creative } from "../../schemas/creativeSchema";
import {
  useBrandCreativesInfinite,
  BrandCreativesInfiniteFilters,
} from "../../hooks/useBrandCreativesInfinite";
import CreativeList, {
  CREATIVE_STATUS_BADGE,
  CreativeDetailRow,
} from "../../components/subComponents/CreativeList";
import {
  formatTimestampToDate,
  getCreativeStatus,
} from "../../utils/dateUtils";

const STATUS_TABS = [
  { label: "All", value: "" },
  { label: "Active", value: "ACTIVE" },
  { label: "Upcoming", value: "UPCOMING" },
  { label: "Expired", value: "EXPIRED" },
];

/**
 * The brand portal's only screen. Read-only: a brand cannot upload, edit or
 * delete — the tenant owns the creatives and the scheduling. It passes no row
 * actions, no bulk actions and no detail actions to CreativeList, so there is
 * nothing here that could mutate even if a permission were mis-granted.
 */
const BrandCreatives: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<Creative | null>(null);
  const [filters, setFilters] = useState<BrandCreativesInfiniteFilters>({});

  const {
    creatives,
    total,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useBrandCreativesInfinite(filters);

  const detailRows: CreativeDetailRow[] = selectedFile
    ? [
        { label: "Name", value: selectedFile.media?.[0]?.fileName || "-" },
        {
          label: "Start Date",
          value: selectedFile.startDate
            ? formatTimestampToDate(selectedFile.startDate)
            : "-",
        },
        {
          label: "End Date",
          value: selectedFile.endDate
            ? formatTimestampToDate(selectedFile.endDate)
            : "-",
        },
        {
          label: "Status",
          value:
            CREATIVE_STATUS_BADGE[
              getCreativeStatus(selectedFile.startDate, selectedFile.endDate)
            ].label,
        },
        {
          label: "Resolution",
          value: selectedFile.media?.[0]?.resolution || "N/A",
        },
        {
          label: "Duration",
          value: selectedFile.media?.[0]?.duration
            ? `${selectedFile.media[0].duration}s`
            : "N/A",
        },
        { label: "Description", value: selectedFile.description || "-" },
      ]
    : [];

  return (
    <div className="flex flex-col px-6 py-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold text-gray-800">My Creatives</h1>
        <span className="text-sm text-gray-500">
          {total} creative{total === 1 ? "" : "s"}
        </span>
      </div>

      <CreativeList
        creatives={creatives}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={(searchTerm) =>
          setFilters((prev) => ({ ...prev, search: searchTerm }))
        }
        activeType={filters.type}
        onTypeChange={(type) =>
          setFilters((prev) => {
            const { type: _dropped, ...rest } = prev;
            return type ? { ...rest, type } : rest;
          })
        }
        selectedCreative={selectedFile}
        onSelectCreative={setSelectedFile}
        detailRows={detailRows}
        toolbarSlot={
          <>
            <span className="mx-3 h-6 w-px bg-gray-300" />

            {STATUS_TABS.map((tab) => {
              const isActive = (filters.status ?? "") === tab.value;
              return (
                <button
                  key={tab.value || "all"}
                  onClick={() =>
                    setFilters((prev) => {
                      const { status: _dropped, ...rest } = prev;
                      return tab.value ? { ...rest, status: tab.value } : rest;
                    })
                  }
                  className={`px-3 py-2 mx-1 rounded border text-sm font-medium transition-all duration-150 ${
                    isActive
                      ? "bg-primary-default text-white border-primary-default"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </>
        }
      />
    </div>
  );
};

export default BrandCreatives;
